import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { IntakeSourcesConfig } from "@sales-ops-amplify/shared";
import {
  getDiscoveryProfile,
  saveDiscoveryProfile,
  type DiscoveryProfilePatch,
} from "@/api/intake-sources";
import {
  addDiscoveredOffers,
  getScanHistory,
  type ExploreOffer,
} from "@/api/discovery";

const DISCOVERY_QUERY_KEY = ["discovery"] as const;

export function useDiscoveryProfile() {
  return useQuery({
    queryKey: [...DISCOVERY_QUERY_KEY, "profile"],
    queryFn: getDiscoveryProfile,
    staleTime: 30_000,
  });
}

export function useSaveDiscoveryProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: DiscoveryProfilePatch) => saveDiscoveryProfile(patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: DISCOVERY_QUERY_KEY });
    },
  });
}

export function useScanHistory(params?: { limit?: number; offset?: number }) {
  return useQuery({
    queryKey: [...DISCOVERY_QUERY_KEY, "history", params ?? {}],
    queryFn: () => getScanHistory(params),
    staleTime: 60_000,
  });
}

export function useAddDiscoveredOffer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      offers,
      source,
    }: {
      offers: ExploreOffer[];
      source?: string;
    }) => addDiscoveredOffers(offers, source),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["opportunities"] });
      qc.invalidateQueries({ queryKey: ["pipeline"] });
      qc.invalidateQueries({ queryKey: DISCOVERY_QUERY_KEY });
    },
  });
}

export function enabledCompanyCount(
  profile: IntakeSourcesConfig | null | undefined,
): number {
  return (
    profile?.trackedCompanies?.filter(
      (c) => c.enabled !== false && c.ats,
    ).length ?? 0
  );
}

export function enabledProviderCount(
  profile: IntakeSourcesConfig | null | undefined,
): number {
  return profile?.signalSources?.length ?? 0;
}
